import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../firebase";
import {
  Typography,
  Container,
  Card,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import LaunchIcon from "@mui/icons-material/Launch";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import OnboardingFlow from "../components/OnboardingFlow";

function LoginSuccess() {
  const [user, loading] = useAuthState(auth);
  const [token, setToken] = useState("");
  const [creatingUser, setCreatingUser] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // If no user is logged in, redirect to login page
    if (!loading && !user) {
      navigate("/login");
      return;
    }

    // If user is logged in, check if they've completed onboarding
    if (user) {
      checkOnboardingStatus();
    }
  }, [user, loading, navigate]);

  const checkOnboardingStatus = async () => {
    try {
      setCreatingUser(true);

      // Check if user exists in Firestore
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      let userData = null;

      // If user doesn't exist, create a new user document
      if (!userDoc.exists()) {
        const newUser = {
          blocks: [],
          dateJoined: new Date().toISOString(),
          displayName: user.displayName || user.email,
          email: user.email,
          friendRequests: [],
          friends: [],
          photoURL: user.photoURL || "",
          tags: [],
          paymentStatus: "free",
          socials: {
            discord: "",
            instagram: "",
            snapchat: "",
          },
          onboardingCompleted: false, // NEW FIELD
        };

        await setDoc(userDocRef, newUser);
        userData = newUser;
      } else {
        userData = userDoc.data();
      }

      // Check if onboarding is completed
      if (userData.onboardingCompleted) {
        // User has completed onboarding, redirect to download page
        generateTokenAndOpenApp();
      } else {
        // User needs to complete onboarding
        setCheckingOnboarding(false);
        setShowOnboarding(true);
      }

      setCreatingUser(false);
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      setCreatingUser(false);
      setCheckingOnboarding(false);
      setShowOnboarding(true);
    }
  };

  const handleOnboardingComplete = async (profileData) => {
    try {
      // Update user document with onboarding data
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        onboardingCompleted: true,
        onboardingDate: new Date().toISOString(),
        profile: profileData,
      });

      // Also sync to whitelist collection if user has paid status
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      if (userData.paymentStatus === "paid") {
        // User has paid, add/update in whitelist collection
        await syncToWhitelist(userData, profileData);
      }

      // Generate token and deep-link back to the app
      generateTokenAndOpenApp();
    } catch (error) {
      console.error("Error completing onboarding:", error);
    }
  };

  const syncToWhitelist = async (userData, profileData) => {
    try {
      // Check if user already exists in whitelist by email
      const { collection, query, where, getDocs } = await import("firebase/firestore");
      const whitelistRef = collection(db, "whitelist");
      const q = query(whitelistRef, where("email", "==", user.email));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Update existing whitelist entry
        const whitelistDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, "whitelist", whitelistDoc.id), {
          googleUid: user.uid,
          onboardingCompleted: true,
          profile: profileData,
          onboardingDate: new Date().toISOString(),
        });
      } else {
        // Create new whitelist entry
        await setDoc(doc(collection(db, "whitelist")), {
          email: user.email,
          googleUid: user.uid,
          onboardingCompleted: true,
          profile: profileData,
          paymentStatus: userData.paymentStatus,
          dateJoined: new Date().toISOString(),
          onboardingDate: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error syncing to whitelist:", error);
    }
  };

  const generateTokenAndOpenApp = async () => {
    try {
      // Get the Firebase Cloud Functions instance
      const functions = getFunctions();

      // Generate custom token using the deployed function
      try {
        console.log("Calling generateCustomToken");
        const generateCustomToken = httpsCallable(functions, "generateCustomToken");
        const result = await generateCustomToken({ uid: user.uid });

        // Extract the customToken from the result
        const customToken = result.data.customToken;
        setToken(customToken);

        // Immediately try to open app once we have the custom token
        setTimeout(() => {
          openApp(customToken);
        }, 500);
      } catch (tokenError) {
        console.error("Error generating custom token:", tokenError);
        // Fallback to ID token if custom token generation fails
        const idToken = await user.getIdToken();
        setToken(idToken);

        setTimeout(() => {
          openApp(idToken);
        }, 500);
      }
    } catch (error) {
      console.error("Error in token generation:", error);
    }
  };

  const openApp = (idToken) => {
    const tokenToUse = idToken || token;
    const appUrl = `edutools://auth?token=${encodeURIComponent(tokenToUse)}`;

    // Try to open using a hidden anchor tag
    try {
      const link = document.createElement("a");
      link.style.display = "none";
      link.href = appUrl;
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    } catch (e) {
      // Fallback to traditional method if needed
      window.location.href = appUrl;
    }
  };

  // Show onboarding flow
  if (showOnboarding) {
    return <OnboardingFlow onComplete={handleOnboardingComplete} userEmail={user?.email} />;
  }

  // Show loading state
  if (loading || creatingUser || checkingOnboarding || !token) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress color="primary" />
        <Typography variant="h6" sx={{ mt: 2, color: "white" }}>
          {creatingUser || checkingOnboarding
            ? "Setting up your account..."
            : "Preparing your login..."}
        </Typography>
      </Container>
    );
  }

  // Show app launch screen
  return (
    <div className="page-wrapper">
      <Navbar />

      <div
        className="section-hero wf-section"
        id="hero-section"
        style={{ minHeight: "100vh", display: "flex", alignItems: "center" }}
      >
        <div
          className="container-default w-container"
          style={{ width: "100%" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Card
              sx={{
                maxWidth: "500px",
                width: "100%",
                background:
                  "radial-gradient(circle at 50% 200%, #34353919, #17181a00 61%), radial-gradient(circle at 50% -60%, #3435394a, #17181a00 75%)",
                backdropFilter: "blur(10px)",
                borderRadius: "16px",
                border: "1px solid rgba(255, 255, 255, 0.102)",
                padding: "40px",
                boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
                color: "white",
                textAlign: "center",
                margin: "0 16px",
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  marginBottom: "16px",
                }}
              >
                Login Successful
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  marginBottom: "32px",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "1.1rem",
                }}
              >
                Opening the EduTools app...
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    fontSize: "0.95rem",
                    mb: 2,
                  }}
                >
                  If the app doesn't open automatically, your browser might have
                  blocked the popup.
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<LaunchIcon />}
                  onClick={() => openApp()}
                  sx={{
                    textTransform: "none",
                    fontSize: "1.1rem",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    background:
                      "linear-gradient(135deg, rgba(0, 153, 255, 0.2), rgba(0, 102, 255, 0.2))",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 8px 32px 0 rgba(0, 102, 255, 0.15)",
                    color: "#fff",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 40px 0 rgba(0, 102, 255, 0.25)",
                      background:
                        "linear-gradient(135deg, rgba(0, 153, 255, 0.3), rgba(0, 102, 255, 0.3))",
                    },
                  }}
                >
                  Open EduTools Now
                </Button>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 3,
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "0.9rem",
                  }}
                >
                  If clicking the button doesn't work, please make sure you have
                  the EduTools app{" "}
                  <Link
                    to="/download"
                    style={{
                      color: "rgba(77, 171, 245, 0.9)",
                      textDecoration: "underline",
                      transition: "color 0.2s ease",
                    }}
                  >
                    installed
                  </Link>{" "}
                  on your device.
                </Typography>
              </Box>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginSuccess;
